import { dateTime } from "@/lib/fmt";
import { TimeInput } from "@/components/app/TimeInput";
import { useState } from "react";
import { toast } from "sonner";
import { History, Pencil, Trash2, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth, useGet } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const inp = "w-full h-11 rounded-lg border border-input bg-white px-3 text-sm";
const locked = (c) => ["submitted", "finalized"].includes(c.status);

function EditDialog({ c, onClose, onDone }) {
  const [f, setF] = useState({ name: c.name, date: c.date, effective_time: c.effective_time || "23:59", reason: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/inv/counts/${c.id}`, { ...f, rev: c.rev }); toast.success(locked(c) ? "Saved — results for affected periods were recalculated" : "Saved"); onDone(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="bg-white max-w-md space-y-3" data-testid="count-edit-dialog">
      <DialogTitle className="font-display text-2xl">Edit count details</DialogTitle>
      <label className="text-xs text-stone-600">Name<input className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="count-edit-name" /></label>
      <div className="grid grid-cols-2 gap-2"><label className="text-xs text-stone-600">Inventory date<input type="date" className={inp} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="count-edit-date" /></label>
        <label className="text-xs text-stone-600">Effective time<TimeInput className={inp} value={f.effective_time} onChange={(e) => setF({ ...f, effective_time: e.target.value })} data-testid="count-edit-time" /></label></div>
      <p className="text-[11px] text-stone-500">The count represents stock at close of business on this date. Deliveries, waste and sales dated that day are treated as before the count.</p>
      {locked(c) && <input className={inp} placeholder="Reason (required for submitted/finalized counts)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="count-edit-reason" />}
      <Button className="h-11 w-full" disabled={busy || (locked(c) && !f.reason.trim())} onClick={save} data-testid="count-edit-save">{busy ? "Saving…" : "Save"}</Button>
    </DialogContent></Dialog>
  );
}

function HistoryDialog({ c, onClose }) {
  const { data } = useGet(`/inv/counts/${c.id}/history`);
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid="count-history-dialog">
      <DialogTitle className="font-display text-2xl">Change history</DialogTitle>
      {!data ? <p className="text-sm text-stone-500">Loading…</p> : <ul className="text-sm divide-y">{data.history.map((h, i) => (
        <li key={`${h.at}-${i}`} className="py-2" data-testid={`count-history-${i}`}><b className="capitalize">{h.action}</b> · {h.by} · {dateTime(h.at)}
          {h.reason && <div className="text-xs text-stone-600">Reason: {h.reason}</div>}
          {h.before && <div className="text-xs text-stone-500">Was: {Object.entries(h.before).map(([k, v]) => `${k} ${v}`).join(", ")} → {Object.entries(h.after || {}).map(([k, v]) => `${k} ${v}`).join(", ")}</div>}</li>))}</ul>}
      {data?.versions?.length > 0 && <p className="text-xs text-stone-500">{data.versions.length} prior version(s) preserved.</p>}
    </DialogContent></Dialog>
  );
}

export function CountActions({ c, onDone, compact }) {
  const { has } = useAuth();
  const [dlg, setDlg] = useState(null);
  const canApprove = has("inventory", "approve");
  const canEdit = has("inventory", "edit") && (!locked(c) || canApprove);
  const post = async (path, ok) => {
    const reason = window.prompt(`Reason for ${path === "remove" ? "removing" : "restoring"} “${c.name}”?`);
    if (!reason?.trim()) return;
    try { await api.post(`/inv/counts/${c.id}/${path}`, { reason }); toast.success(ok); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const b = `h-9 px-2 text-xs ${compact ? "" : "sm:px-3"}`;
  return (
    <div className="flex flex-wrap gap-1" onClick={(e) => e.preventDefault()} data-testid={`count-actions-${c.id}`}>
      {canEdit && !c.removed && <Button variant="outline" className={b} onClick={() => setDlg("edit")} data-testid={`count-edit-${c.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit details</Button>}
      <Button variant="outline" className={b} onClick={() => setDlg("hist")} data-testid={`count-history-btn-${c.id}`}><History className="h-3.5 w-3.5 mr-1" />History</Button>
      {!c.removed && (locked(c) ? canApprove : has("inventory", "edit")) && <Button variant="outline" className={`${b} text-rose-700`} onClick={() => post("remove", "Removed — it no longer affects inventory results")} data-testid={`count-remove-${c.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</Button>}
      {c.removed && canApprove && <Button variant="outline" className={b} onClick={() => post("restore", "Restored")} data-testid={`count-restore-${c.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button>}
      {dlg === "edit" && <EditDialog c={c} onClose={() => setDlg(null)} onDone={onDone} />}
      {dlg === "hist" && <HistoryDialog c={c} onClose={() => setDlg(null)} />}
    </div>
  );
}
